import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Card, CardBody, Tab, Tabs } from '@structural-climbing/ui'
import {
  competitionBoards,
  type CompetitionDiscipline,
  type Discipline,
} from '@structural-climbing/validation'
import type { GymSection } from '../routes/types'
import { CompetitionRouteDetailModal } from './CompetitionRouteDetailModal'
import { ConvertRouteModal } from './ConvertRouteModal'
import { RouteIdentity } from './RouteIdentity'
import {
  competitionState,
  routeDiscipline,
  type CompetitionRound,
  type CompetitionState,
  type ProvenanceRoute,
} from './types'

interface Props {
  discipline: CompetitionDiscipline
  /** The competition's sections, for grading each route on its own scale. */
  sections: GymSection[]
  /**
   * The number each route carries in the competition, by route id. A route set
   * for a competition has no grade, so this is what tells two of them apart.
   */
  labels: Record<string, string>
  /**
   * Whether the competition is over. Until then a route is in play, so its
   * keep-or-archive decision is locked, unless its own round has already
   * closed. The Worker refuses the same way.
   */
  ended: boolean
  /**
   * The competition's rounds. Empty or missing for one that does not use
   * them; then nothing opens before the end.
   */
  rounds?: CompetitionRound[]
  /** Gym-local wall clock, for deciding which rounds have closed. */
  localNow?: string
  /** The round each route sits in, by route id. null = the whole competition. */
  roundIds?: Record<string, string | null>
  /** Routes created for this competition, in any review state. */
  routes: ProvenanceRoute[]
  onRefresh: () => void
}

type ReviewState = 'pending' | 'converted' | 'archived'

function reviewState(route: ProvenanceRoute): ReviewState {
  if (route.status === 'archived') return 'archived'
  return route.competition_only ? 'pending' : 'converted'
}

/** What still needs a decision comes first; what is settled sinks. */
const STATE_ORDER: Record<ReviewState, number> = { pending: 0, converted: 1, archived: 2 }

const STATE_STYLES: Record<ReviewState, string> = {
  pending: 'bg-warning/15 text-warning-700',
  converted: 'bg-success/15 text-success-700',
  archived: 'bg-default/40 text-foreground/60',
}

/** Same chips the Routes and Rounds tabs put on a round. */
const ROUND_STYLES: Record<CompetitionState, string> = {
  upcoming: 'bg-default/40 text-foreground/60',
  in_progress: 'bg-success/15 text-success-700',
  ended: 'bg-default/40 text-foreground/60',
}

const ROUND_KEYS: Record<CompetitionState, string> = {
  upcoming: 'competitions.round_upcoming',
  in_progress: 'competitions.round_live',
  ended: 'competitions.round_ended',
}

export function ReviewRoutesTab({
  discipline,
  sections,
  labels,
  ended,
  rounds = [],
  localNow = '',
  roundIds = {},
  routes,
  onRefresh,
}: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [busyId, setBusyId] = useState<string | null>(null)
  const [converting, setConverting] = useState<ProvenanceRoute | null>(null)
  const [detail, setDetail] = useState<ProvenanceRoute | null>(null)
  // The wall on show in a combined competition, one tab each as on Routes.
  const [board, setBoard] = useState<Discipline>(competitionBoards(discipline)[0])

  async function patchRoute(routeId: string, body: Record<string, unknown>) {
    setBusyId(routeId)
    try {
      const token = await getAccessTokenSilently()
      await fetch(`${apiUrl}/climbing-routes/${routeId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      onRefresh()
    } finally {
      setBusyId(null)
    }
  }

  if (routes.length === 0) {
    return (
      <Card className="shadow-sm">
        <CardBody className="py-8 text-center text-foreground/60">
          {t('competitions.reviewEmpty')}
        </CardBody>
      </Card>
    )
  }

  const pending = routes.filter((r) => reviewState(r) === 'pending').length
  const boards = competitionBoards(discipline)
  const combined = boards.length > 1
  const disciplineOf = (route: ProvenanceRoute) =>
    routeDiscipline(discipline, route.section_id, sections)
  // Numbers restart per discipline in a combined competition, so "1" alone
  // would not say which wall.
  const titleFor = (route: ProvenanceRoute) => {
    const label = labels[route.id] ?? ''
    return combined ? `${t(`competitions.discipline_${disciplineOf(route)}`)} · ${label}` : label
  }
  const labelNumber = (route: ProvenanceRoute) =>
    Number.parseInt(labels[route.id] ?? '', 10) || Number.MAX_SAFE_INTEGER
  const sorted = [...routes].sort(
    (a, b) =>
      STATE_ORDER[reviewState(a)] - STATE_ORDER[reviewState(b)] || labelNumber(a) - labelNumber(b),
  )
  // Set for the competition, then taken out of it. It has no number and no
  // tops, and can look exactly like the route that replaced it, so it is kept
  // apart: converting one by mistake puts the same route on the wall twice.
  const isRemoved = (route: ProvenanceRoute) => !(route.id in labels)
  const onBoard = combined ? sorted.filter((r) => disciplineOf(r) === board) : sorted
  const inSet = onBoard.filter((r) => !isRemoved(r))
  const removed = onBoard.filter(isRemoved)

  const roundStates = new Map(rounds.map((r) => [r.id, competitionState(r, localNow)]))
  const roundOf = (route: ProvenanceRoute) => {
    const id = roundIds[route.id]
    return id && roundStates.has(id) ? id : null
  }
  // A route in a round is out of play once that round closes, so it can be
  // decided then. One on the whole competition waits for the end.
  const reviewable = (route: ProvenanceRoute) =>
    ended || roundStates.get(roundOf(route) ?? '') === 'ended'

  // By round, in the rounds' own order, with whatever runs the whole
  // competition last. One unnamed group when there are no rounds.
  const roundGroups = (items: ProvenanceRoute[]) =>
    rounds.length
      ? [
          ...rounds.map((round) => ({
            key: round.id,
            name: round.name,
            state: roundStates.get(round.id) ?? null,
            items: items.filter((r) => roundOf(r) === round.id),
          })),
          {
            key: 'all',
            name: t('competitions.roundAll'),
            state: null,
            items: items.filter((r) => roundOf(r) === null),
          },
        ].filter((group) => group.items.length > 0)
      : [{ key: 'all', name: '', state: null, items }]

  function renderRow(route: ProvenanceRoute) {
    const state = reviewState(route)
    const open = reviewable(route)
    return (
      <li
        key={route.id}
        className="flex flex-wrap items-center gap-2 rounded-lg border border-default-200 p-2 sm:flex-nowrap sm:gap-3"
      >
        {/* Fills the row, so pressing it opens the route. A competition route
            has no grade until it is converted; then there is simply nothing
            to show, not a "no grade" label. */}
        <Button
          variant="light"
          className="h-auto min-w-0 flex-1 justify-start gap-2 whitespace-normal p-1 text-left sm:gap-3"
          aria-label={t('competitions.viewRouteDetails', { label: titleFor(route) })}
          onPress={() => setDetail(route)}
        >
          {labels[route.id] && (
            <span className="rounded bg-foreground/10 px-2 py-0.5 text-sm font-bold">
              {labels[route.id]}
            </span>
          )}
          <RouteIdentity route={route} apiUrl={apiUrl} discipline={disciplineOf(route)} />
        </Button>

        {/* A line of their own on a phone: beside the route they would squeeze
            its photo down to a sliver. */}
        <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:ml-auto sm:w-auto sm:flex-none sm:flex-nowrap">
          {isRemoved(route) && (
            <span className="rounded-full bg-danger/15 px-2 py-0.5 text-xs font-medium text-danger-700">
              {t('competitions.review_removed')}
            </span>
          )}
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATE_STYLES[state]}`}>
            {t(`competitions.review_${state}`)}
          </span>
          {state === 'pending' && (
            <div className="flex gap-2">
              <Button
                size="sm"
                color="success"
                variant="flat"
                isLoading={busyId === route.id}
                isDisabled={!open}
                onPress={() => setConverting(route)}
              >
                {t('competitions.convertRoute')}
              </Button>
              <Button
                size="sm"
                color="danger"
                variant="flat"
                isLoading={busyId === route.id}
                isDisabled={!open}
                onPress={() => void patchRoute(route.id, { status: 'archived' })}
              >
                {t('competitions.archiveRoute')}
              </Button>
            </div>
          )}
        </div>
      </li>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-foreground/60">{t('competitions.reviewHint')}</p>
      {!ended ? (
        <div className="flex gap-3 rounded-xl border border-primary/25 bg-primary/5 p-4">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mt-0.5 h-5 w-5 shrink-0 text-primary"
            aria-hidden="true"
          >
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-semibold">
              {t(
                rounds.length
                  ? 'competitions.reviewLockedRoundsTitle'
                  : 'competitions.reviewLockedTitle',
              )}
            </p>
            <p className="text-sm text-foreground/70">
              {t(
                rounds.length
                  ? 'competitions.reviewLockedRoundsBody'
                  : 'competitions.reviewLockedBody',
              )}
            </p>
          </div>
        </div>
      ) : (
        pending > 0 && (
          <p className="rounded-lg bg-warning/10 p-3 text-sm text-warning-700">
            {t('competitions.reviewPendingWarning', { count: pending })}
          </p>
        )
      )}

      {combined && (
        <Tabs
          aria-label={t('routes.sectionDiscipline')}
          selectedKey={board}
          onSelectionChange={(key) => setBoard(key as Discipline)}
        >
          {boards.map((b) => (
            <Tab
              key={b}
              title={
                <span className="flex items-center gap-2">
                  {t(`competitions.discipline_${b}`)}
                  <span className="rounded-full bg-default-100 px-2 py-0.5 text-xs font-medium text-foreground/60">
                    {sorted.filter((r) => disciplineOf(r) === b).length}
                  </span>
                </span>
              }
            />
          ))}
        </Tabs>
      )}

      {roundGroups(inSet).map((group) => (
        <div key={group.key} className="flex flex-col gap-2">
          {group.name && (
            <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
              {group.name}
              {group.state && (
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${ROUND_STYLES[group.state]}`}
                >
                  {t(ROUND_KEYS[group.state])}
                </span>
              )}
            </h3>
          )}
          <ul className="flex flex-col gap-2">{group.items.map(renderRow)}</ul>
        </div>
      ))}

      {removed.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="flex items-center gap-2 border-b border-divider pb-2 text-base font-bold">
            {t('competitions.reviewRemovedTitle')}
            <span className="rounded-full bg-default-100 px-2 py-0.5 text-xs font-medium text-foreground/60">
              {removed.length}
            </span>
          </h2>
          <p className="text-sm text-foreground/60">{t('competitions.reviewRemovedHint')}</p>
          <ul className="flex flex-col gap-2">{removed.map(renderRow)}</ul>
        </section>
      )}

      <CompetitionRouteDetailModal
        route={
          detail && {
            ...detail,
            section_id: detail.section_id ?? null,
            setter_note: detail.setter_note ?? null,
          }
        }
        title={detail ? titleFor(detail) : ''}
        discipline={detail ? disciplineOf(detail) : boards[0]}
        sectionName={sections.find((s) => s.id === detail?.section_id)?.name}
        apiUrl={apiUrl}
        onClose={() => setDetail(null)}
      />
      <ConvertRouteModal
        route={converting}
        discipline={converting ? disciplineOf(converting) : 'boulder'}
        removed={converting ? isRemoved(converting) : false}
        onClose={() => setConverting(null)}
        onConverted={onRefresh}
      />
    </div>
  )
}
